'use client'
// MissionControl — pile verticale.
//   DIGITAL CORE · ALERTES & FLUX · AGENTS
import { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import { AgentMeshPanel } from './AgentMeshPanel'
import { NPCWorkforcePanel } from './NPCWorkforcePanel'
import { HumanRequestsPanel } from './HumanRequestsPanel'
import { NewsSummary } from '@/components/landing/NewsCard'
import type { AiNews } from '@/types/landing'
import type { CityProfile } from './GlobeCore'

const GlobeCore = dynamic(() => import('@/components/command/GlobeCore'), {
  ssr: false,
  loading: () => (
    <div style={{
      height: 320, display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 12,
    }}>
      Chargement du globe…
    </div>
  ),
})

function useIsWide(min = 1100) {
  const [wide, setWide] = useState(true)
  useEffect(() => {
    const on = () => setWide(window.innerWidth >= min)
    on()
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [min])
  return wide
}

function ColHeader({ channel, title }: { channel: string; title: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
      paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.06)',
    }}>
      <span style={{
        fontSize: 9, fontFamily: 'var(--font-mono)', letterSpacing: '0.12em',
        color: 'var(--accent-cyan)', background: 'rgba(0,212,255,0.08)',
        border: '1px solid rgba(0,212,255,0.18)', borderRadius: 6, padding: '2px 7px',
      }}>
        {channel}
      </span>
      <span className="font-display" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
        {title}
      </span>
    </div>
  )
}

function StatRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 8, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', lineHeight: 1.3 }}>
        {value}
      </div>
    </div>
  )
}

function CityStatPanel({ city, side }: { city: CityProfile | null; side: 'left' | 'right' }) {
  const style: React.CSSProperties = {
    flex: '0 0 96px', minWidth: 0,
    display: 'flex', flexDirection: 'column', justifyContent: 'center',
    padding: '6px 4px',
    borderRadius: 8,
  }

  if (!city) return <div style={style} />

  const fmtPop = (n: number | null | undefined) =>
    n ? n.toLocaleString('fr-CA') : null

  const fmtDate = (d: string | null | undefined) => {
    if (!d) return null
    const y = new Date(d).getFullYear()
    return isNaN(y) ? null : `${y}`
  }

  const fmtIncome = (n: number | null | undefined) =>
    n ? `${Math.round(n / 1000)}k$` : null

  const fmtRate = (n: number | null | undefined) =>
    n != null ? `${n.toFixed(1)}%` : null

  if (side === 'left') {
    return (
      <div style={style}>
        <StatRow label="Population"  value={fmtPop(city.population)} />
        <StatRow label="Fondée en"   value={fmtDate(city.incorporationDate)} />
        <StatRow label="MRC"         value={city.mrc} />
        <StatRow label="Maire"       value={city.mayor} />
      </div>
    )
  }

  return (
    <div style={{ ...style, alignItems: 'flex-end' }}>
      <StatRow label="Région"       value={city.regionAdmin} />
      <StatRow label="Rev. médian"  value={fmtIncome(city.medianIncome)} />
      <StatRow label="Chômage"      value={fmtRate(city.unemploymentRate)} />
      <StatRow label="Santé (CIUSSS)" value={city.ciusss} />
    </div>
  )
}

function DigitalCore() {
  const [cityData, setCityData] = useState<CityProfile | null>(null)

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <span style={{
          fontSize: 9, fontFamily: 'var(--font-mono)', letterSpacing: '0.12em',
          color: 'var(--accent-cyan)', background: 'rgba(0,212,255,0.08)',
          border: '1px solid rgba(0,212,255,0.18)', borderRadius: 6, padding: '2px 7px',
        }}>CH-02</span>
        <span className="font-display" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
          Digital Core
        </span>
        <a href="/technologue" style={{ marginLeft: 'auto', fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--accent)', textDecoration: 'none' }}>
          Jumeau →
        </a>
      </div>

      {/* Globe + city details side panels */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <CityStatPanel city={cityData} side="left" />

        <div style={{
          flex: 1, display: 'flex', justifyContent: 'center',
          background: 'rgba(0,20,60,0.4)',
          border: '1px solid rgba(0,120,255,0.12)',
          borderRadius: 12, padding: '12px 8px 4px', overflow: 'hidden',
        }}>
          <GlobeCore onCityData={setCityData} />
        </div>

        <CityStatPanel city={cityData} side="right" />
      </div>
    </div>
  )
}

export function MissionControl({ news }: { news: AiNews | null }) {
  const wide = useIsWide()

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 48 }}>

      {/* DIGITAL CORE — globe + données ville */}
      <div className="hud-card" style={{ padding: '20px', minWidth: 0 }}>
        <DigitalCore />
      </div>

      {/* ALERTES & FLUX */}
      <div className="hud-card" style={{ padding: '20px', minWidth: 0 }}>
        <ColHeader channel="CH-03" title="Alertes & flux" />
        <HumanRequestsPanel />
        <div style={wide ? {
          display: 'grid', gridTemplateColumns: '1fr auto', gap: 20, alignItems: 'start',
        } : {}}>
          <NewsSummary news={news} />
          <a href="/actualites" style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            marginTop: wide ? 0 : 8,
            fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--accent)',
            textDecoration: 'none', letterSpacing: '0.08em', whiteSpace: 'nowrap',
            alignSelf: 'flex-start',
          }}>Articles régionaux →</a>
        </div>
      </div>

      {/* AGENTS — GPU Mesh + NPC Workforce */}
      <div className="hud-card" style={{ padding: '20px', minWidth: 0 }}>
        <ColHeader channel="CH-01" title="Agents" />
        <div style={wide ? {
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'start',
        } : { display: 'flex', flexDirection: 'column', gap: 16 }}>
          <AgentMeshPanel />
          <NPCWorkforcePanel />
        </div>
      </div>

    </div>
  )
}
